import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Users, UserPlus, Shield, Building2, Search, Filter,
  ChevronDown, Edit2, Trash2, ToggleLeft, ToggleRight,
  Key, X, Check, AlertCircle, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUtilisateurs = () => {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filterRole, setFilterRole] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCabinet, setFilterCabinet] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    nom: '',
    prenom: '',
    telephone: '',
    role: 'assistant',
    fonction: '',
    cabinet_id: '',
    password: '',
    permissions: null
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [token]);

  // Load users when filters change
  useEffect(() => {
    if (token) loadUsers();
  }, [filterRole, filterCategory, filterCabinet, token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, modulesRes, cabinetsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users/modules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cabinets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      setRoles(rolesRes.data.roles || []);
      setModules(modulesRes.data.modules || []);
      setCabinets(cabinetsRes.data || []);
      setStats(statsRes.data || {});
      
      await loadUsers();
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRole) params.append('role', filterRole);
      if (filterCategory) params.append('category', filterCategory);
      if (filterCabinet) params.append('cabinet_id', filterCabinet);
      
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadPermissionsTemplate = async (role) => {
    try {
      const res = await axios.get(`${API}/admin/users/permissions-template/${role}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, permissions: res.data.permissions }));
    } catch (err) {
      console.error('Error loading permissions template:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const res = await axios.post(`${API}/admin/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTempPassword(res.data.temporary_password);
      setSuccess('Utilisateur créé avec succès');
      loadUsers();
      loadData();
      
      // Don't close modal yet - show temp password
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Utilisateur mis à jour');
      setShowEditModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePermissions = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/permissions`, formData.permissions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Permissions mises à jour');
      setShowPermissionsModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour des permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(res.data.message);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Réinitialiser le mot de passe de cet utilisateur ?')) return;
    
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTempPassword(res.data.temporary_password);
      setSuccess('Mot de passe réinitialisé');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer définitivement cet utilisateur ?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Utilisateur supprimé');
      loadUsers();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openCreateModal = () => {
    setFormData({
      email: '',
      nom: '',
      prenom: '',
      telephone: '',
      role: roles.length > 0 ? roles[0].id : 'assistant',
      fonction: '',
      cabinet_id: '',
      password: '',
      permissions: null
    });
    setTempPassword(null);
    setError(null);
    setShowCreateModal(true);
    
    // Load default permissions for first role
    if (roles.length > 0) {
      loadPermissionsTemplate(roles[0].id);
    }
  };

  const openEditModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      email: userToEdit.email || '',
      nom: userToEdit.nom || '',
      prenom: userToEdit.prenom || '',
      telephone: userToEdit.telephone || '',
      role: userToEdit.role || 'assistant',
      fonction: userToEdit.fonction || '',
      cabinet_id: userToEdit.cabinet_id || '',
      permissions: userToEdit.permissions || null
    });
    setError(null);
    setShowEditModal(true);
  };

  const openPermissionsModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData(prev => ({ ...prev, permissions: userToEdit.permissions || {} }));
    setError(null);
    setShowPermissionsModal(true);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'superadmin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-purple-100 text-purple-800',
      'collaborateur_syndicappli': 'bg-blue-100 text-blue-800',
      'directeur_cabinet': 'bg-amber-100 text-amber-800',
      'gestionnaire': 'bg-green-100 text-green-800',
      'assistant': 'bg-gray-100 text-gray-800',
      'coproprietaire': 'bg-cyan-100 text-cyan-800',
      'prestataire': 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'superadmin': 'Super Admin',
      'admin': 'Admin',
      'collaborateur_syndicappli': 'Collaborateur',
      'directeur_cabinet': 'Directeur de cabinet',
      'gestionnaire': 'Gestionnaire',
      'assistant': 'Assistant',
      'coproprietaire': 'Copropriétaire',
      'prestataire': 'Prestataire'
    };
    return labels[role] || role;
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.nom?.toLowerCase().includes(query) ||
      u.prenom?.toLowerCase().includes(query) ||
      u.fonction?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#00D3A8] mr-2" size={24} />
        <span>Chargement...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <p className="text-gray-500 mt-1">Créez et gérez les accès utilisateurs</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
          data-testid="create-user-btn"
        >
          <UserPlus size={20} />
          <span>Nouvel utilisateur</span>
        </button>
      </div>

      {/* Success/Error messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Check size={20} />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}

      {/* Temp Password Alert */}
      {tempPassword && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Key size={20} />
            <span className="font-semibold">Mot de passe temporaire</span>
          </div>
          <div className="flex items-center space-x-2">
            <code className="bg-white px-3 py-1 rounded border text-lg font-mono">
              {showPassword ? tempPassword : '••••••••••••'}
            </code>
            <button onClick={() => setShowPassword(!showPassword)} className="text-amber-600 hover:text-amber-800">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            <button 
              onClick={() => navigator.clipboard.writeText(tempPassword)}
              className="text-xs bg-amber-200 px-2 py-1 rounded hover:bg-amber-300"
            >
              Copier
            </button>
          </div>
          <p className="text-sm mt-2">Communiquez ce mot de passe à l&apos;utilisateur. Il devra le changer à la première connexion.</p>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_users || 0}</p>
                <p className="text-sm text-gray-500">Total utilisateurs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ToggleRight className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active_users || 0}</p>
                <p className="text-sm text-gray-500">Actifs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_role?.superadmin || stats.by_role?.admin || 0}</p>
                <p className="text-sm text-gray-500">Super Admins</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Building2 className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_cabinets || 0}</p>
                <p className="text-sm text-gray-500">Cabinets</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher par nom, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
            />
          </div>
          
          {/* Category filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] appearance-none bg-white"
            >
              <option value="">Tous les types</option>
              <option value="syndicappli">Équipe Syndicappli</option>
              <option value="cabinet">Cabinets clients</option>
              <option value="resident">Copropriétaires</option>
              <option value="external">Prestataires</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          </div>
          
          {/* Role filter */}
          <div className="relative">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] appearance-none bg-white pr-8"
            >
              <option value="">Tous les rôles</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          </div>
          
          {/* Cabinet filter - only for admins */}
          {user?.role === 'admin' && cabinets.length > 0 && (
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <select
                value={filterCabinet}
                onChange={(e) => setFilterCabinet(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] appearance-none bg-white"
              >
                <option value="">Tous les cabinets</option>
                {cabinets.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Utilisateur</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Rôle</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Cabinet</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Fonction</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Statut</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">{u.prenom} {u.nom}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                      {getRoleLabel(u.role)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {u.cabinet_name || '-'}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {u.fonction || '-'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      u.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {u.is_active !== false ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openPermissionsModal(u)}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Gérer les permissions"
                      >
                        <Shield size={18} />
                      </button>
                      <button
                        onClick={() => openEditModal(u)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleResetPassword(u.id)}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Réinitialiser mot de passe"
                      >
                        <Key size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(u.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          u.is_active !== false 
                            ? 'text-orange-600 hover:bg-orange-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={u.is_active !== false ? 'Désactiver' : 'Activer'}
                      >
                        {u.is_active !== false ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Nouvel utilisateur</h2>
                <button onClick={() => { setShowCreateModal(false); setTempPassword(null); }} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                <select
                  value={formData.role}
                  onChange={(e) => {
                    setFormData({...formData, role: e.target.value});
                    loadPermissionsTemplate(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                >
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name} - {r.description}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                <input
                  type="text"
                  placeholder="Ex: Support technique, Commercial..."
                  value={formData.fonction}
                  onChange={(e) => setFormData({...formData, fonction: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              {/* Cabinet selection - always visible for admin, conditional for directors */}
              {(user?.role === 'admin' || ['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cabinet
                    {['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <select
                    value={formData.cabinet_id}
                    onChange={(e) => setFormData({...formData, cabinet_id: e.target.value})}
                    required={['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  >
                    <option value="">
                      {['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role) 
                        ? 'Sélectionner un cabinet (obligatoire)' 
                        : 'Aucun cabinet (optionnel)'}
                    </option>
                    {cabinets.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                  {['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Les {formData.role === 'directeur_cabinet' ? 'directeurs' : formData.role === 'gestionnaire' ? 'gestionnaires' : 'assistants'} doivent être affiliés à un cabinet
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe initial</label>
                <input
                  type="text"
                  placeholder="Laisser vide pour génération automatique"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
                <p className="text-xs text-gray-500 mt-1">Si vide, un mot de passe temporaire sera généré</p>
              </div>
              
              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setTempPassword(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {tempPassword ? 'Fermer' : 'Annuler'}
                </button>
                {!tempPassword && (
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
                  >
                    {saving ? 'Création...' : 'Créer'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Modifier l&apos;utilisateur</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                >
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                <input
                  type="text"
                  value={formData.fonction}
                  onChange={(e) => setFormData({...formData, fonction: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                />
              </div>
              
              {/* Cabinet selection in edit mode */}
              {user?.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cabinet
                    {['directeur_cabinet', 'gestionnaire', 'assistant'].includes(formData.role) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <select
                    value={formData.cabinet_id}
                    onChange={(e) => setFormData({...formData, cabinet_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  >
                    <option value="">Aucun cabinet</option>
                    {cabinets.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Permissions</h2>
                  <p className="text-gray-500">{selectedUser.prenom} {selectedUser.nom}</p>
                </div>
                <button onClick={() => setShowPermissionsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Configurez les permissions d&apos;accès aux différents modules de l&apos;application.
                </p>
              </div>
              
              <div className="space-y-4">
                {modules.map(module => {
                  const modulePerms = formData.permissions?.modules?.[module.id] || {};
                  
                  return (
                    <div key={module.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{module.name}</h4>
                          <p className="text-sm text-gray-500">{module.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2">
                        {['read', 'create', 'update', 'delete'].map(action => {
                          const actionLabels = {
                            read: 'Lecture',
                            create: 'Création',
                            update: 'Modification',
                            delete: 'Suppression'
                          };
                          
                          const isChecked = modulePerms[action] || false;
                          
                          return (
                            <label key={action} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const newPermissions = {...formData.permissions};
                                  if (!newPermissions.modules) newPermissions.modules = {};
                                  if (!newPermissions.modules[module.id]) newPermissions.modules[module.id] = {};
                                  newPermissions.modules[module.id][action] = e.target.checked;
                                  setFormData({...formData, permissions: newPermissions});
                                }}
                                className="w-4 h-4 text-[#00D3A8] border-gray-300 rounded focus:ring-[#00D3A8]"
                              />
                              <span className="text-sm text-gray-600">{actionLabels[action]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm mt-4">
                  {error}
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowPermissionsModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpdatePermissions}
                  disabled={saving}
                  className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les permissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUtilisateurs;
